import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';

interface HIDScannerOptions {
  /** Callback when scanner input is detected */
  onScanComplete: (scannedData: string) => void;
  /** Minimum typing speed to consider it a scanner (chars/second) */
  minCharactersPerSecond?: number;
  /** Time to wait after last character before processing (ms) */
  timeout?: number;
  /** Minimum characters to consider valid scan (ignores speed if met) */
  minLength?: number;
}

interface HIDScannerContextType {
  /** Whether scanner input is currently being detected */
  isScanning: boolean;
  /** Whether the scanner listener is active */
  isReady: boolean;
  /** Set/update the scanner callback and options */
  setScannerOptions: (options: HIDScannerOptions) => void;
  /** Clear the current callback (no-op for compatibility) */
  clearCallback: () => void;
}

const HIDScannerContext = createContext<HIDScannerContextType | undefined>(undefined);

interface HIDScannerProviderProps {
  children: ReactNode;
}

/**
 * Global HID Scanner Context
 *
 * Provides a global keyboard listener for HID scanners (RFID readers, barcode scanners)
 * that emulate keyboard input. The listener persists across component remounts,
 * solving the issue where the scanner stops working after form resets.
 *
 * Key differences from local useHIDScanner hook:
 * - Event listener is registered ONCE at app level
 * - Callbacks are stored in a ref, not as dependencies
 * - Listener persists even when components unmount/remount
 * - No duplicate listeners or race conditions
 */
export const HIDScannerProvider: React.FC<HIDScannerProviderProps> = ({ children }) => {
  // State
  const [isScanning, setIsScanning] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Refs - these persist across component remounts
  const bufferRef = useRef<string>('');
  const keystrokeTimesRef = useRef<number[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastProcessedDataRef = useRef<string>('');
  const lastProcessedTimeRef = useRef<number>(0);
  const isProcessingRef = useRef(false);

  // Ref for callback - always points to the latest callback
  const callbacksRef = useRef<HIDScannerOptions | null>(null);

  // Ref for options
  const optionsRef = useRef<HIDScannerOptions>({
    onScanComplete: () => {}, // Placeholder, will be replaced by setScannerOptions
    minCharactersPerSecond: 20,
    timeout: 100,
    minLength: 10,
  });

  // Diagnostic: Track provider instance
  const instanceId = useRef(`HIDScannerProvider-${Date.now()}`);
  console.log(`[${instanceId.current}] HIDScannerProvider initialized`);

  /**
   * Calculate typing speed based on recent keystrokes
   */
  const getTypingSpeed = useCallback(() => {
    const times = keystrokeTimesRef.current;
    if (times.length < 2) return 0;

    const now = Date.now();
    // Only consider keystrokes in the last second
    const recentTimes = times.filter((t) => now - t < 1000);

    if (recentTimes.length < 2) return 0;

    const timeSpan = (recentTimes[recentTimes.length - 1] - recentTimes[0]) / 1000;
    const speed = recentTimes.length / (timeSpan || 0.001);

    return speed;
  }, []);

  /**
   * Process the accumulated buffer
   * Uses callbacksRef to always get the latest callback
   */
  const processBuffer = useCallback(() => {
    if (isProcessingRef.current) return;

    const data = bufferRef.current.trim();
    const options = optionsRef.current;

    // Prevent duplicate processing within 1 second
    const now = Date.now();
    if (data === lastProcessedDataRef.current && now - lastProcessedTimeRef.current < 1000) {
      bufferRef.current = '';
      keystrokeTimesRef.current = [];
      setIsScanning(false);
      return;
    }

    // Ignore if empty or too short
    if (!data || data.length < (options.minLength || 10)) {
      bufferRef.current = '';
      keystrokeTimesRef.current = [];
      setIsScanning(false);
      return;
    }

    isProcessingRef.current = true;
    lastProcessedDataRef.current = data;
    lastProcessedTimeRef.current = now;
    setIsScanning(false);

    console.log(`[${instanceId.current}] Processing scan:`, data);

    // Call the callback from the ref (always the latest)
    if (callbacksRef.current?.onScanComplete) {
      callbacksRef.current.onScanComplete(data);
    }

    // Reset processing flag after a short delay
    setTimeout(() => {
      isProcessingRef.current = false;
    }, 500);

    // Clear buffer and keystroke times
    bufferRef.current = '';
    keystrokeTimesRef.current = [];
  }, []); // No dependencies - uses refs internally

  /**
   * Clear everything and reset state
   */
  const resetScanner = useCallback(() => {
    bufferRef.current = '';
    keystrokeTimesRef.current = [];
    setIsScanning(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  /**
   * Set/update the scanner callback and options
   * This is called by components to register their callback
   */
  const setScannerOptions = useCallback((options: HIDScannerOptions) => {
    console.log(`[${instanceId.current}] Updating scanner callback`);
    callbacksRef.current = options;
    optionsRef.current = {
      ...options,
      minCharactersPerSecond: options.minCharactersPerSecond || 20,
      timeout: options.timeout || 100,
      minLength: options.minLength || 10,
    };
  }, []);

  /**
   * Clear the current callback (for cleanup)
   */
  const clearCallback = useCallback(() => {
    console.log(`[${instanceId.current}] Clearing scanner callback`);
    callbacksRef.current = null;
  }, []);

  /**
   * Global keyboard event listener
   * Registered ONCE when the provider mounts
   */
  useEffect(() => {
    setIsReady(true);

    const handleKeyDown = (e: KeyboardEvent) => {
      const now = Date.now();
      const options = optionsRef.current;

      // If no callback is registered, ignore scanner input
      if (!callbacksRef.current) {
        return;
      }

      // Ignore modifier keys only
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      // Skip if user is typing in ANY input field
      const target = e.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();

      // Skip for all input fields, textareas, and contenteditable elements
      if (tagName === 'input' || tagName === 'textarea' || target.isContentEditable) {
        return;
      }

      // Skip for select elements too
      if (tagName === 'select') {
        return;
      }

      // Handle Enter key - scanner terminator
      if (e.key === 'Enter') {
        if (bufferRef.current.length > 0) {
          e.preventDefault();
          e.stopPropagation();

          console.log(`[${instanceId.current}] Enter key detected, buffer length:`, bufferRef.current.length);

          // Clear any pending timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }

          // Check if we have valid data
          if (bufferRef.current.length >= (options.minLength || 10)) {
            processBuffer();
          } else {
            console.log(`[${instanceId.current}] Enter pressed but buffer too short, clearing`);
            resetScanner();
          }
        }
        return;
      }

      // Track keystroke timing
      keystrokeTimesRef.current.push(now);
      if (keystrokeTimesRef.current.length > 20) {
        keystrokeTimesRef.current.shift();
      }

      // Calculate typing speed
      const speed = getTypingSpeed();
      const isHighSpeed = speed >= (options.minCharactersPerSecond || 20);

      // Capture all printable characters
      if (e.key.length === 1) {
        // If this looks like a scanner, prevent default
        if (isHighSpeed || bufferRef.current.length > 0) {
          e.preventDefault();
          e.stopPropagation();
        }

        bufferRef.current += e.key;

        // Set scanning state
        if (isHighSpeed || bufferRef.current.length >= (options.minLength || 10)) {
          setIsScanning(true);
        }

        // Clear existing timeout and set new one
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          const currentSpeed = getTypingSpeed();
          if (currentSpeed >= (options.minCharactersPerSecond || 20) || bufferRef.current.length >= (options.minLength || 10)) {
            console.log(`[${instanceId.current}] Timeout reached, processing (speed:`, currentSpeed.toFixed(1), ')');
            processBuffer();
          } else {
            console.log(`[${instanceId.current}] Timeout but not scanner input, clearing`);
            resetScanner();
          }
        }, options.timeout || 100);
      }
    };

    // Add event listener with capture phase to intercept before other handlers
    document.addEventListener('keydown', handleKeyDown, true);

    console.log(`[${instanceId.current}] Global keyboard listener attached`);

    return () => {
      console.log(`[${instanceId.current}] HIDScannerProvider UNMOUNTING - removing keyboard listener`);
      document.removeEventListener('keydown', handleKeyDown, true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [getTypingSpeed, processBuffer, resetScanner]);

  const value: HIDScannerContextType = {
    isScanning,
    isReady,
    setScannerOptions,
    clearCallback
  };

  return (
    <HIDScannerContext.Provider value={value}>
      {children}
    </HIDScannerContext.Provider>
  );
};

export const useHIDScannerContext = () => {
  const context = useContext(HIDScannerContext);
  if (context === undefined) {
    throw new Error('useHIDScannerContext must be used within a HIDScannerProvider');
  }
  return context;
};

/**
 * Hook for components to register their HID scanner callback
 * This is the replacement for the local useHIDScanner hook
 *
 * Usage:
 * ```tsx
 * const { isScanning, isReady } = useHIDScanner({
 *   onScanComplete: (data) => console.log('Scanned:', data),
 *   enabled: true,
 * });
 * ```
 */
export const useHIDScanner = (options: {
  onScanComplete: (scannedData: string) => void;
  minCharactersPerSecond?: number;
  timeout?: number;
  enabled?: boolean;
  minLength?: number;
}) => {
  const context = useHIDScannerContext();
  const { setScannerOptions, clearCallback, isScanning, isReady } = context;
  const optionsRef = useRef(options);

  // Update options ref when options change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Register callback when enabled
  useEffect(() => {
    if (options.enabled !== false) {
      setScannerOptions({
        onScanComplete: options.onScanComplete,
        minCharactersPerSecond: options.minCharactersPerSecond,
        timeout: options.timeout,
        minLength: options.minLength,
      });

      return () => {
        // No need to clear on unmount - let the next component register its callback
      };
    }
  }, [
    options.enabled,
    options.onScanComplete,
    options.minCharactersPerSecond,
    options.timeout,
    options.minLength,
    setScannerOptions,
  ]);

  return {
    isScanning,
    isReady,
  };
};

export default useHIDScanner;
